/**
 * Pattern Matching System Types
 * Sistema de reglas visuales para mapear grupos de filas CSV a transacciones
 */

// ========================================================================================
// FIELD TRANSFORMS - Transformaciones de valores
// ========================================================================================

export type TransformType =
  | 'abs' // Valor absoluto
  | 'uppercase' // Convertir a mayúsculas
  | 'lowercase' // Convertir a minúsculas
  | 'trim' // Eliminar espacios
  | 'parseNumber' // Parsear como número
  | 'parseDate' // Parsear como fecha
  | 'replace' // Reemplazar texto
  | 'concat' // Concatenar valores
  | 'split' // Dividir texto
  | 'prefix' // Añadir prefijo
  | 'suffix' // Añadir sufijo
  | 'negate' // Negar valor numérico
  | 'extractBase' // Extraer moneda base de par (BTCUSDT → BTC)
  | 'extractQuote' // Extraer moneda quote de par (BTCUSDT → USDT)
  | 'assetAlias' // Look up alias via template.assetAliases (e.g. XXBT → BTC, ZEUR → EUR)
  | 'krakenAsset' // DEPRECATED alias of 'assetAlias' — kept because persisted DB templates reference it

export interface FieldTransform {
  type: TransformType
  config?: Record<string, unknown> // Configuración específica del transform
  params?: Record<string, unknown> // Legacy/seeded container — DB-seeded templates (exchange_api_templates.pattern_rules) persist this instead of `config`, with `pattern`/`replacement` spellings for 'replace'
}

// Minimal structural input for `canonicalizeTransformConfig`. `type` is widened
// to `string` so backend engines that define their OWN TransformType unions
// (e.g. platform exchange-integration) can call the canonicalizer without casts.
export interface CanonicalizableFieldTransform {
  type: string
  config?: Record<string, unknown>
  params?: Record<string, unknown>
}

/**
 * ONE canonical way to read a transform's configuration. Total: never throws.
 *
 * - Container precedence: `config` (contract) wins over `params` (legacy/seeded).
 * - For 'replace', key names diverged historically: the contract spells
 *   `search`/`replace`, seeded templates spell `pattern`/`replacement`.
 *   The result always carries the contract spelling; contract keys win.
 */
export function canonicalizeTransformConfig(
  transform: CanonicalizableFieldTransform,
): Record<string, unknown> | undefined {
  const chosenContainer = transform.config ?? transform.params
  if (chosenContainer === undefined) {
    return undefined
  }
  if (transform.type !== 'replace') {
    return chosenContainer
  }
  // Normalize 'replace' keys to the contract spelling (search/replace).
  return {
    ...chosenContainer,
    search: chosenContainer['search'] ?? chosenContainer['pattern'],
    replace: chosenContainer['replace'] ?? chosenContainer['replacement'],
  }
}

// ========================================================================================
// ROW CONDITIONS - Condiciones sobre filas
// ========================================================================================

export type RowConditionOperator =
  | 'equals' // Igual a
  | 'not_equals' // Diferente de
  | 'contains' // Contiene texto
  | 'not_contains' // No contiene texto
  | 'starts_with' // Empieza con
  | 'ends_with' // Termina con
  | 'in' // Está en lista
  | 'not_in' // No está en lista
  | 'gt' // Mayor que (números)
  | 'lt' // Menor que (números)
  | 'gte' // Mayor o igual (números)
  | 'lte' // Menor o igual (números)
  | 'regex' // Expresión regular
  | 'empty' // Campo vacío
  | 'not_empty' // Campo no vacío

export interface RowCondition {
  field: string // Nombre de la columna CSV
  operator: RowConditionOperator // Operador de comparación
  value?: string | string[] // Valor(es) a comparar
  caseSensitive?: boolean // Case sensitive (default: false)
  regex?: string // Regex pattern (si operator es 'regex')
}

// ========================================================================================
// ROW SELECTORS - Selectores de filas
// ========================================================================================

export type RowSelectorType =
  | 'first' // Primera fila del grupo
  | 'last' // Última fila del grupo
  | 'any' // Cualquier fila
  | 'conditional' // Fila que cumple condición
  | 'aggregate' // Agregar múltiples filas

export type AggregateMethod =
  | 'sum' // Sumar valores
  | 'average' // Promedio
  | 'min' // Mínimo
  | 'max' // Máximo
  | 'count' // Contar filas
  | 'concat' // Concatenar textos
  | 'first' // Primer valor
  | 'last' // Último valor

export interface ConditionalSelector {
  type: 'conditional'
  condition: RowCondition
}

export interface AggregateSelector {
  type: 'aggregate'
  method: AggregateMethod
  filter?: RowCondition // Opcional: filtrar qué filas agregar
}

export interface SimpleSelector {
  type: 'first' | 'last' | 'any'
}

export type RowSelector = SimpleSelector | ConditionalSelector | AggregateSelector

// ========================================================================================
// PATTERN FIELD MAPPINGS - Mapeo de campos
// ========================================================================================

export interface PatternFieldMapping {
  targetField: string // Campo destino en transaction (ej: 'incomingAsset')
  rowSelector: RowSelector // De qué fila(s) tomar el valor
  sourceColumn: string // Columna CSV a usar
  defaultValue?: string // Valor por defecto si no se encuentra
  transforms?: FieldTransform[] // Transformaciones a aplicar
  isRequired?: boolean // Si el campo es requerido
}

// ========================================================================================
// PATTERN MATCH CONDITIONS - Condiciones para aplicar patrón
// ========================================================================================

export interface RowCountCondition {
  min?: number // Mínimo de filas en el grupo
  max?: number // Máximo de filas en el grupo
}

export interface RequiredRowsCondition {
  column: string // Columna a verificar
  values: string[] // Valores que deben existir
  matchAll?: boolean // true = todos los valores deben existir, false = al menos uno
}

export interface PatternMatchConditions {
  rowCount?: RowCountCondition // Condición sobre número de filas
  requiredRows?: RequiredRowsCondition[] // Filas que deben existir en el grupo
  customConditions?: RowCondition[] // Condiciones adicionales opcionales
}

// ========================================================================================
// PATTERN RULE - Regla completa de patrón
// ========================================================================================

export type TransactionType =
  | 'trade'
  | 'deposit'
  | 'withdrawal'
  | 'fee'
  | 'staking'
  | 'staking_reward'
  | 'mining'
  | 'mining_reward'
  | 'airdrop'
  | 'transfer_in'
  | 'transfer_out'
  | 'interest'
  | 'dividend'
  | 'expense'
  | 'income'
  | 'lost'
  | 'stolen'

export interface PatternRule {
  id: string // Identificador único
  name: string // Nombre descriptivo
  description?: string // Descripción opcional
  priority: number // Prioridad (mayor = más prioritario)
  isEnabled: boolean // Si la regla está activa

  // Condiciones
  matchConditions: PatternMatchConditions

  // Resultado
  transactionType: TransactionType // Tipo de transacción a generar

  // Mapeo de campos
  fieldMappings: PatternFieldMapping[] // Cómo extraer cada campo

  // Metadata
  createdAt?: string // ISO timestamp
  updatedAt?: string // ISO timestamp
  usageCount?: number // Cuántas veces se ha aplicado
  successRate?: number // % de éxito (0-1)
}

// ========================================================================================
// PATTERN MATCHING RESULT - Resultado de aplicar patrón
// ========================================================================================

export interface PatternMatchResult {
  matched: boolean // Si el grupo coincidió con algún patrón
  rule?: PatternRule // Regla que aplicó
  extractedData?: Record<string, unknown> // Datos extraídos
  errors?: string[] // Errores durante extracción
  warnings?: string[] // Advertencias
}

// ========================================================================================
// PATTERN RULE TEMPLATE - Template con reglas pre-configuradas
// ========================================================================================

export interface PatternRuleTemplate {
  id: string
  name: string
  description: string
  exchange: string // Exchange al que aplica (binance, coinbase, etc.)
  rules: PatternRule[] // Lista de reglas del template
  version: string // Versión del template
  createdAt: string
  updatedAt: string
}

// ========================================================================================
// UI HELPER TYPES - Tipos de ayuda para el frontend
// ========================================================================================

export interface PatternRuleEditorState {
  rule: PatternRule
  exampleGroup?: {
    rows: Record<string, unknown>[]
    preview: Record<string, unknown>
  }
  validationErrors: string[]
  isDirty: boolean
}

export interface RowSelectorUIConfig {
  type: RowSelectorType
  label: string
  description: string
  requiresCondition: boolean
  requiresAggregation: boolean
}

export interface TransformUIConfig {
  type: TransformType
  label: string
  description: string
  requiresConfig: boolean
  configFields?: Array<{
    name: string
    type: 'text' | 'number' | 'select'
    label: string
    options?: string[]
  }>
}

// ========================================================================================
// EXPORTS
// ========================================================================================

// Helper functions type guards
export function isConditionalSelector(selector: RowSelector): selector is ConditionalSelector {
  return selector.type === 'conditional'
}

export function isAggregateSelector(selector: RowSelector): selector is AggregateSelector {
  return selector.type === 'aggregate'
}

export function isSimpleSelector(selector: RowSelector): selector is SimpleSelector {
  return selector.type === 'first' || selector.type === 'last' || selector.type === 'any'
}
